import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { minutesToTime, timeToMinutes } from '@motoschool/shared';
import { api, ApiRequestError } from '../lib/api';
import { useAuth } from '../auth/AuthContext';
import { LESSON_DURATIONS, useTeachers } from '../lib/useTeachers';
import { Button, ErrorNotice, Field, Input, RadioGroup, Select, Sheet } from './ui';
import { TimeChips } from './TimeChips';

interface CreateSlotSheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** Дата, выбранная в ленте дней — подставляется в форму. */
  defaultDate: string;
  timeZone: string;
  /** Инструктор, выбранный в фильтре расписания. */
  defaultTeacherId?: string | null;
}

/**
 * Собирает момент времени из локальных даты и времени, введённых в поясе школы.
 * Браузер интерпретировал бы ввод в своём поясе, и занятие уехало бы на несколько часов.
 */
function toUtcIso(date: string, time: string, timeZone: string): string {
  const naive = new Date(`${date}T${time}:00Z`);
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  const parts: Record<string, string> = {};
  for (const part of formatter.formatToParts(naive)) {
    if (part.type !== 'literal') parts[part.type] = part.value;
  }

  const asZoned = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour) % 24,
    Number(parts.minute),
    Number(parts.second),
  );

  const offset = asZoned - naive.getTime();
  return new Date(naive.getTime() - offset).toISOString();
}

/**
 * Занять время в расписании вручную.
 *
 * Расписание инструктор не нарезает: он задаёт часы работы, а свободное время
 * считается как график минус занятое. Эта форма нужна для другого — закрыть
 * час, о котором договорились не через сайт, или просто заблокировать время.
 * Длительность здесь спрашивается именно потому, что случай разовый.
 */
export function CreateSlotSheet({
  isOpen,
  onClose,
  defaultDate,
  timeZone,
  defaultTeacherId,
}: CreateSlotSheetProps): React.JSX.Element {
  const queryClient = useQueryClient();
  const { user, hasRole } = useAuth();
  const isAdmin = hasRole('ADMIN');

  const { data: teachers } = useTeachers(isOpen);
  const teacherOptions = teachers?.items ?? [];

  const [error, setError] = useState<string | null>(null);
  // Сервер отклонил время из-за графика — предлагаем занять его осознанно
  const [canOverride, setCanOverride] = useState(false);

  // Инструктор ведёт расписание за себя, администратор — за выбранного
  const [teacherId, setTeacherId] = useState<string>(defaultTeacherId ?? '');

  const [form, setForm] = useState({
    date: defaultDate,
    time: '10:00',
    duration: 60,
    price: '',
  });

  // Дата из ленты дней меняется, пока шторка закрыта
  useEffect(() => {
    if (!isOpen) return;
    setForm((previous) => ({ ...previous, date: defaultDate }));
  }, [isOpen, defaultDate]);

  // Администратору подставляем первого инструктора, чтобы поле не было пустым
  useEffect(() => {
    if (!isAdmin) return;
    if (teacherId) return;
    const fallback = defaultTeacherId ?? teacherOptions[0]?.id;
    if (fallback) setTeacherId(fallback);
  }, [isAdmin, teacherId, defaultTeacherId, teacherOptions]);

  const priceToMinor = (value: string): number | null => {
    const parsed = Number(value.replace(',', '.'));
    return value.trim() === '' || Number.isNaN(parsed) ? null : Math.round(parsed * 100);
  };

  const close = (): void => {
    setError(null);
    setCanOverride(false);
    onClose();
  };

  const create = useMutation({
    // Тип аргумента задаём явно: со значением по умолчанию react-query
    // выводит его как void и вызвать mutate(true) уже нельзя
    mutationFn: async (ignoreWorkingHours: boolean) => {
      if (isAdmin && !teacherId) throw new Error('Выберите инструктора');

      const startsAt = toUtcIso(form.date, form.time, timeZone);
      const endsAt = new Date(new Date(startsAt).getTime() + form.duration * 60_000).toISOString();

      return api.post('/slots', {
        // Администратор обязан указать инструктора, инструктор занимает своё время
        teacherId: isAdmin ? teacherId || undefined : undefined,
        startsAt,
        endsAt,
        priceMinor: priceToMinor(form.price),
        ignoreWorkingHours,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['slots'] });
      close();
    },
    onError: (caught: unknown) => {
      if (caught instanceof ApiRequestError) {
        setError(caught.message);
        const reason = (caught.details as { reason?: string } | undefined)?.reason;
        setCanOverride(reason === 'OUTSIDE_WORKING_HOURS' || reason === 'DAY_OFF');
        return;
      }
      setError('Не удалось занять время');
      setCanOverride(false);
    },
  });

  const durationLabel = (minutes: number): string =>
    LESSON_DURATIONS.find((item) => item.value === minutes)?.label ?? `${minutes} мин`;

  // Итог: во сколько занятие закончится
  const endTime = minutesToTime(timeToMinutes(form.time) + form.duration);

  return (
    <Sheet
      isOpen={isOpen}
      onClose={close}
      title="Занять время"
      footer={
        <div className="flex gap-2 pb-1">
          <Button variant="secondary" fullWidth onClick={close} type="button">
            Закрыть
          </Button>
          <Button
            fullWidth
            isLoading={create.isPending}
            onClick={() => {
              setError(null);
              setCanOverride(false);
              create.mutate(false);
            }}
          >
            Занять
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="rounded-xl bg-canvas px-3 py-2 text-sm text-muted">
          Свободное время ученики выбирают сами по вашему графику работы. Здесь время
          только закрывается — под занятие, о котором договорились не через сайт.
        </p>

        {error && <ErrorNotice message={error} />}
        {canOverride && (
          <Button
            variant="secondary"
            fullWidth
            isLoading={create.isPending}
            onClick={() => {
              setError(null);
              setCanOverride(false);
              create.mutate(true);
            }}
          >
            Всё равно занять время вне графика
          </Button>
        )}

        {isAdmin ? (
          <Field label="Инструктор" required>
            <Select value={teacherId} onChange={(event) => setTeacherId(event.target.value)}>
              <option value="">Выберите инструктора</option>
              {teacherOptions.map((teacher) => (
                <option key={teacher.id} value={teacher.id}>
                  {teacher.name}
                </option>
              ))}
            </Select>
          </Field>
        ) : (
          <p className="rounded-xl bg-canvas px-3 py-2 text-sm text-muted">
            Время закрывается в вашем расписании — {user?.name}
          </p>
        )}

        <Field label="Дата" required>
          <Input
            type="date"
            value={form.date}
            onChange={(event) => setForm({ ...form, date: event.target.value })}
          />
        </Field>

        <Field label="Начало" required>
          <TimeChips value={form.time} onChange={(value) => setForm({ ...form, time: value })} />
        </Field>

        <Field label="Длительность">
          <RadioGroup
            name="Длительность занятия"
            value={form.duration}
            options={LESSON_DURATIONS.map((item) => ({ ...item }))}
            onChange={(value) => setForm({ ...form, duration: value })}
          />
        </Field>

        {/* Итог виден до сохранения — не нужно считать время окончания в уме */}
        <p className="rounded-xl bg-brand-50 px-3 py-2 text-center text-sm text-brand-700">
          Занято{' '}
          <b className="tabular-nums">
            {form.time} — {endTime}
          </b>
          , {durationLabel(form.duration)}
        </p>

        <Field label="Цена занятия" hint="Пусто — берётся ставка инструктора">
          <Input
            value={form.price}
            onChange={(event) => setForm({ ...form, price: event.target.value })}
            inputMode="decimal"
            placeholder="8000"
          />
        </Field>
      </div>
    </Sheet>
  );
}
